'use client';

import { Box, Stack, Typography } from '@mui/material';
import { DonutBlock, SectionTable } from './TickerPortfolioTabVisuals';
import type { PortfolioTabViewModel } from './TickerPortfolioTabClient';

type Props = {
  data: PortfolioTabViewModel;
  chartColors: string[];
  groupCount: (labels: string[]) => Array<{ label: string; value: number }>;
  groupSum: (items: Array<{ label: string; value: number | null }>) => Array<{ label: string; value: number }>;
  formatOptionalNumber: (value: number | null) => string;
  formatOptionalCurrency: (value: number | null) => string;
};

export function PortfolioEquitiesSection({
  data,
  chartColors,
  groupCount,
  groupSum,
  formatOptionalNumber,
  formatOptionalCurrency,
}: Props) {
  if (data.relatedEquities.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem ações/participações societárias reportadas na CVM para o período mais recente.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.2,
        }}
      >
        <DonutBlock
          title='Participações por tipo (qtd.)'
          items={groupCount(data.relatedEquities.map((item) => item.type?.trim() || 'Sem tipo'))}
          colors={chartColors}
          infoText='Quantidade de participações societárias por tipo.'
        />
        <DonutBlock
          title='Participações por tipo (valor)'
          items={groupSum(
            data.relatedEquities.map((item) => ({
              label: item.type?.trim() || 'Sem tipo',
              value: item.value,
            })),
          )}
          colors={chartColors}
          valueKind='currency'
          infoText='Valor das participações societárias por tipo.'
        />
      </Box>
      <SectionTable
        headers={['Companhia/Sociedade', 'Tipo', 'Quantidade', 'Valor']}
        rows={data.relatedEquities.map((item) => [
          item.company,
          item.type ?? '-',
          formatOptionalNumber(item.quantity),
          formatOptionalCurrency(item.value),
        ])}
      />
    </Stack>
  );
}
